import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Settings, Code, MessageCircle } from "lucide-react";
import ChatWidget from "./ChatWidget";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { tenantAPI } from "../utils/api";
import { logger } from '../utils/logger';

const EmbedDemo = () => {
  const { tenant } = useAuth();
  const [widgetSettings, setWidgetSettings] = useState(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  // Fetch widget settings for the logged-in tenant
  useEffect(() => {
    const fetchWidgetSettings = async () => {
      if (!tenant?.id) return;
      try {
        const settings = await tenantAPI.getWidgetSettings(tenant.id);
        setWidgetSettings(settings);
        setIsWidgetReady(true);
      } catch (error) {
        logger.error('Error fetching widget settings:', error);
        // Still allow widget to render, it will use fallback behavior
        setIsWidgetReady(true);
      }
    };
    fetchWidgetSettings();
  }, [tenant?.id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--g6-secondary)] via-[#005580] to-[var(--g6-primary)]">
      {/* Navigation */}
      <nav className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[var(--g6-primary)] rounded-full flex items-center justify-center font-bold text-white text-lg">
              G6
            </div>
            <span className="text-white font-bold text-xl">G6th Sense Chat Widget</span>
          </div>
          <Link to="/admin">
            <Button className="bg-white text-[var(--g6-secondary)] hover:bg-gray-100">
              <Settings className="w-4 h-4 mr-2" />
              Admin Panel
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h1 className="text-6xl font-bold text-white mb-6 leading-tight">
            AI-Powered Chat Widget
            <br />
            for Your Website
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Engage visitors with intelligent conversations, automated responses, and seamless agent handoff.
            Trained on your website content and powered by advanced AI.
          </p>
          <div className="flex justify-center space-x-4">
            <Link to="/admin">
              <Button size="lg" className="bg-[var(--g6-primary)] hover:bg-[#008A44] text-white px-8 py-6 text-lg">
                <Settings className="w-5 h-5 mr-2" />
                Configure Widget
              </Button>
            </Link>
            <Link to="/widget">
              <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 px-8 py-6 text-lg">
                <MessageCircle className="w-5 h-5 mr-2" />
                View Fullscreen
              </Button>
            </Link>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="w-14 h-14 bg-[var(--g6-primary)] rounded-xl flex items-center justify-center mb-4">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">AI-Powered Responses</h3>
            <p className="text-white/80 leading-relaxed">
              Trained on your website content using Groq AI for accurate, context-aware responses to visitor questions.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="w-14 h-14 bg-[var(--g6-primary)] rounded-xl flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Smart Agent Handoff</h3>
            <p className="text-white/80 leading-relaxed">
              Automatically detects when visitors need human assistance and seamlessly collects information for your team.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="w-14 h-14 bg-[var(--g6-primary)] rounded-xl flex items-center justify-center mb-4">
              <Code className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Easy Integration</h3>
            <p className="text-white/80 leading-relaxed">
              Simple embed code for any website or WordPress plugin. Configure colors, keywords, and flows through the admin panel.
            </p>
          </div>
        </div>

        {/* Key Features List */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-10 border border-white/20 mb-20">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">What's Included</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[var(--g6-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Three Conversation Flows</h4>
                <p className="text-white/70 text-sm">Information, Sales Enquiry, and Talk to Agent options</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[var(--g6-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Configurable Keywords</h4>
                <p className="text-white/70 text-sm">Trigger agent handoff with custom keywords</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[var(--g6-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Slack Notifications</h4>
                <p className="text-white/70 text-sm">Real-time alerts when visitors request agent assistance</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[var(--g6-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Web Scraping Training</h4>
                <p className="text-white/70 text-sm">Automatically learn from your website content</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[var(--g6-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">User Data Collection</h4>
                <p className="text-white/70 text-sm">Gather name, email, phone, and context for handoffs</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-[var(--g6-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Admin Dashboard</h4>
                <p className="text-white/70 text-sm">Monitor conversations and manage configuration</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <p className="text-white/90 text-lg mb-6">
            Try the chat widget by clicking the button in the bottom-right corner ⬇️
          </p>
          <p className="text-white/70">
            Or visit the <Link to="/admin" className="underline hover:text-white">admin panel</Link> to configure settings
          </p>
        </div>
      </div>

      {/* Embedded Chat Widget - Only render after widget settings are loaded */}
      {isWidgetReady && tenant?.id && widgetSettings?.widget_public_key && (
        <ChatWidget
          embedded={true}
          tenantId={tenant.id}
          widgetPublicKey={widgetSettings.widget_public_key}
        />
      )}
    </div>
  );
};

export default EmbedDemo;
